import { FC } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { type Column as TableColumn } from 'further-ui/components/Table';
import { Transaction } from './types';
import AccountingTable from '../components/AccountingTable';

const TableContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.lg,
  ),
  width: '100%',
  minWidth: 0,
}));

type Props = {
  subtransactions: NonNullable<Transaction['subtransactions']>;
  baseCurrency: CurrencyCode;
};

const getColumns = (
  baseCurrency: CurrencyCode,
): Array<TableColumn<NonNullable<Transaction['subtransactions']>[number]>> => [
  {
    key: 'transactionDate',
    label: 'Date',
    weight: 1,
    textOverflowEllipsis: true,
    render: ({ transactionDate }) => transactionDate.toReadable(),
  },
  {
    key: 'transactionType',
    label: 'Transaction type',
    weight: 1,
    textOverflowEllipsis: true,
  },
  {
    key: 'description',
    label: 'Description',
    weight: 1,
    textOverflowEllipsis: true,
    render: ({ description }) =>
      description || <Typography color="grey.500">--</Typography>,
  },
  {
    key: 'amountIncVatLcy',
    label: 'Amount (LCY)',
    weight: 1,
    textOverflowEllipsis: true,
    render: ({ amountIncVatLcy }) =>
      numberToCurrencyString(amountIncVatLcy, {
        currency: baseCurrency,
        negativeInBrackets: true,
      }),
  },
  {
    key: 'amountIncVatTcy',
    label: 'Amount (TCY)',
    weight: 1,
    textOverflowEllipsis: true,
    render: ({ amountIncVatTcy, transactionCurrency }) =>
      numberToCurrencyString(amountIncVatTcy, {
        currency: transactionCurrency,
        negativeInBrackets: true,
      }),
  },
  {
    key: 'counterpartyLabel',
    label: 'Counterparty',
    weight: 1,
    textOverflowEllipsis: true,
  },
  {
    key: 'bankAccountLabel',
    label: 'Bank account',
    weight: 1,
    textOverflowEllipsis: true,
    render: ({ bankAccountLabel }) =>
      bankAccountLabel || <Typography color="grey.500">--</Typography>,
  },
];

const Subtransactions: FC<Props> = ({ subtransactions, baseCurrency }) => {
  if (subtransactions.length === 0) {
    return null;
  }

  return (
    <TableContainer>
      <AccountingTable
        columns={getColumns(baseCurrency)}
        rows={subtransactions}
        disableSorting
        showHeaderBorder
        showTopBorder
        scrollable
        externalScroll={false}
        withIndentation={false}
      />
    </TableContainer>
  );
};

export default Subtransactions;
